import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UploadSimple, CheckCircle, Warning, FileText, Trash } from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pagina pubblica: il cliente finale carica i documenti richiesti per una pratica.
export default function PublicUploadRequest() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/public/upload-request/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link non valido"));
  }, [token]);

  const submit = async () => {
    if (!files.length) return toast.error("Seleziona almeno un file");
    setSending(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API_BASE}/public/upload-request/${token}`, fd, { timeout: 10 * 60 * 1000 });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il caricamento");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 pt-12 pb-40" data-testid="public-upload-page">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border p-8 space-y-5">
        <div className="text-xs font-semibold uppercase tracking-widest text-violet-700">Housetag · Integrazione documenti</div>
        {error ? (
          <div className="flex items-start gap-3 text-red-700" data-testid="public-upload-error">
            <Warning size={22} weight="fill" className="shrink-0" /> <span>{error}</span>
          </div>
        ) : !info ? (
          <div className="text-sm text-slate-500">Caricamento…</div>
        ) : done ? (
          <div className="flex items-start gap-3 text-emerald-700" data-testid="public-upload-done">
            <CheckCircle size={24} weight="fill" className="shrink-0" />
            <div>
              <div className="font-semibold">Documenti inviati, grazie!</div>
              <div className="text-sm text-slate-600 mt-1">L'operatore Housetag e il tuo professionista sono stati avvisati.</div>
            </div>
          </div>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">{info.service_name}</h1>
              {info.address && <div className="text-sm text-slate-500 mt-1" data-testid="public-upload-address">{info.address}</div>}
            </div>
            <div className="bg-violet-50 border-l-4 border-violet-500 rounded p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-violet-700 mb-1">Documenti richiesti</div>
              <div className="text-sm whitespace-pre-wrap text-slate-800" data-testid="public-upload-message">{info.message}</div>
            </div>
            {info.uploaded_files?.length > 0 && (
              <div className="text-xs text-slate-500" data-testid="public-upload-previous">
                Già caricati: {info.uploaded_files.join(", ")}
              </div>
            )}
            <label className="block border-2 border-dashed border-violet-300 rounded-xl p-6 text-center cursor-pointer hover:bg-violet-50 transition-colors" data-testid="public-upload-dropzone">
              <UploadSimple size={28} className="mx-auto mb-2 text-violet-600" />
              <div className="text-sm font-medium">Clicca per selezionare i file</div>
              <div className="text-xs text-slate-500 mt-1">PDF, immagini o documenti · max 100 MB per file</div>
              <input type="file" multiple className="hidden" data-testid="public-upload-input"
                onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} />
            </label>
            {files.length > 0 && (
              <div className="border rounded-lg divide-y" data-testid="public-upload-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 truncate"><FileText size={14} /> {f.name}</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`public-upload-remove-${i}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button className="w-full ht-gradient border-0 text-white" onClick={submit} disabled={sending || !files.length}
              data-testid="public-upload-submit">
              {sending ? "Invio in corso…" : "Invia documenti"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
